import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { app, obtenerUsuarios, updateById } from "../firebase/firebase";
import AddUserModal from "./Components/AddUserModal";
import EditUserModal from "./Components/EditUserModal";
import { Breadcrumb, Button, Container, Form, InputGroup, Spinner } from 'react-bootstrap';
import { useNavigate } from "react-router-dom";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  FaBookOpen,
  FaChalkboardTeacher,
  FaEnvelope,
  FaPrint,
  FaSearch,
  FaTimes,
  FaUserEdit,
  FaUserGraduate,
  FaUserPlus,
  FaUserShield,
  FaUsers,
} from 'react-icons/fa';
import './MasterView.scss';
import NavbarGlobal from "./NavbarGlobal";
import { prepararUsuariosMaster } from "../utils/usuariosMaster";
import { AppContext } from '../Context';
import { useAlumnosOcultos } from '../hooks/useAlumnosOcultos';
import { BotonVisibilidadAlumno, FiltroAlumnosOcultos } from '../components/alumnos/VisibilidadAlumnos';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'activo', label: 'Activos' },
  { value: 'inactivo', label: 'Inactivos' },
  { value: 'baja-temporal', label: 'Baja temporal' },
  { value: 'baja-definitiva', label: 'Baja definitiva' },
  { value: 'graduado-certificado', label: 'Graduados / Certificados' },
];

const ROLE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'master', label: 'Master' },
  { value: 'teacher', label: 'Profesores' },
  { value: 'student', label: 'Alumnos' },
];

const STATUS_LABELS = {
  activo: 'Activo',
  inactivo: 'Inactivo',
  'baja-temporal': 'Baja temporal',
  'baja-definitiva': 'Baja definitiva',
  'graduado-certificado': 'Graduado / Certificado',
  'sin-estatus': 'Sin estatus',
};

const normalizeText = (value = '') => (
  value
    .toString()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
);

const normalizeStatus = (status = '') => {
  const value = normalizeText(status);

  if (!value) return 'sin-estatus';
  if (value === 'activo' || value === 'activos') return 'activo';
  if (value === 'inactivo' || value === 'inactivos') return 'inactivo';
  if (value === 'baja temporal' || value === 'baja temporalidad') return 'baja-temporal';
  if (value === 'baja definitiva') return 'baja-definitiva';
  if (value.includes('graduado') || value.includes('certificado')) return 'graduado-certificado';

  return value.replace(/\s+/g, '-');
};

const getStatusLabel = (status) => STATUS_LABELS[normalizeStatus(status)] || status || 'Sin estatus';

const getUserName = (usuario) => (
  usuario.displayName || usuario.name || usuario.username || 'Usuario sin nombre'
);

const getUserRole = (usuario) => {
  if (usuario.isMaster) return 'master';
  if (usuario.teacher) return 'teacher';
  return 'student';
};

const getRoleLabel = (role) => {
  if (role === 'master') return 'Master';
  if (role === 'teacher') return 'Profesor';
  return 'Alumno';
};

const getInitials = (name) => {
  const parts = normalizeText(name).split(' ').filter(Boolean);
  const initials = parts.slice(0, 2).map((part) => part[0]).join('');
  return (initials || 'U').toUpperCase();
};

const getAuthAccessLabel = (status) => {
  if (status === 'complete') return 'Acceso listo';
  if (status === 'uid-mismatch') return 'UID inconsistente';
  if (status === 'missing-profile') return 'Sin perfil';
  return 'Sin acceso';
};

function MasterView() {
  const { currentUser } = useContext(AppContext);
  const visibilidad = useAlumnosOcultos(currentUser);
  const { sincronizar } = visibilidad;
  const [loading, setLoading] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const usuariosVisibles = useMemo(() => usuarios.filter(visibilidad.esVisible), [usuarios, visibilidad.esVisible]);
  const cantidadOcultos = useMemo(() => usuarios.filter(visibilidad.estaOculto).length, [usuarios, visibilidad.estaOculto]);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [profesorAsignadoFilter, setProfesorAsignadoFilter] = useState('');
  const [updatingTeacherId, setUpdatingTeacherId] = useState('');
  const navigate = useNavigate();
  const auth = getAuth();
  const db = getFirestore();

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const functions = getFunctions(app, 'us-central1');
      const getUsersAuthStatus = httpsCallable(functions, 'getUsersAuthStatus');
      const [loadedUsers, authStatusResult] = await Promise.all([
        obtenerUsuarios(),
        getUsersAuthStatus({}),
      ]);
      const authUsers = authStatusResult?.data?.authUsers || [];
      setUsuarios(prepararUsuariosMaster(loadedUsers, authUsers));
    } catch (err) {
      console.error('Error on MasterView.loadUsers', err);
      setError('No se pudieron cargar los usuarios.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (!userSnap.exists() || !userSnap.data().isMaster) {
          navigate("/dashboard");
        }
      } else {
        navigate("/login");
      }
    });
    return () => unsubscribe();
  }, [auth, db, navigate]);

  const handleOpenAddModal = useCallback(() => setShowAddModal(true), []);

  const handleOpenEditModal = useCallback((user) => {
    setSelectedUser(user);
    setShowEditModal(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
    loadUsers();
  }, [loadUsers]);

  const handleCloseEditModal = useCallback(() => {
    setShowEditModal(false);
    setSelectedUser(null);
    sincronizar();
    loadUsers();
  }, [loadUsers, sincronizar]);

  const updateIsTeacher = useCallback(async (uid, isTeacher) => {
    try {
      setUpdatingTeacherId(uid);
      await updateById(uid, { teacher: isTeacher });
      setUsuarios((currentUsers) => (
        currentUsers.map((usuario) => (
          usuario.id === uid ? { ...usuario, teacher: isTeacher } : usuario
        ))
      ));
    } catch (err) {
      console.error('Error on MasterView.updateIsTeacher', err);
    } finally {
      setUpdatingTeacherId('');
    }
  }, []);

  const handleCheckChanges = useCallback(async (event, item) => {
    await updateIsTeacher(item.id, event.target.checked);
  }, [updateIsTeacher]);

  const clearFilters = useCallback(() => {
    setSearch('');
    setStatusFilter('');
    setRoleFilter('');
    setProfesorAsignadoFilter('');
  }, []);

  const profesoresUnicos = useMemo(() => {
    const profesores = new Set();

    usuariosVisibles.forEach((usuario) => {
      profesores.add(usuario.asignTeacher || 'No asignado');
    });

    return Array.from(profesores).sort((a, b) => a.localeCompare(b));
  }, [usuariosVisibles]);

  const filteredUsuarios = useMemo(() => {
    const searchTerm = normalizeText(search);

    return usuariosVisibles.filter((usuario) => {
      if (statusFilter && normalizeStatus(usuario.estatus) !== statusFilter) {
        return false;
      }

      if (roleFilter && getUserRole(usuario) !== roleFilter) {
        return false;
      }

      if (profesorAsignadoFilter) {
        const profesorAsignado = usuario.asignTeacher || 'No asignado';
        if (profesorAsignado !== profesorAsignadoFilter) {
          return false;
        }
      }

      if (searchTerm) {
        const searchableValues = [
          usuario.displayName,
          usuario.name,
          usuario.username,
          usuario.email,
          usuario.matricula,
          usuario.asignTeacher,
          usuario.estatus,
        ];

        return searchableValues.some((value) => normalizeText(value).includes(searchTerm));
      }

      return true;
    });
  }, [profesorAsignadoFilter, roleFilter, search, statusFilter, usuariosVisibles]);

  const resumenUsuarios = useMemo(() => {
    const usuariosReales = usuarios.filter(visibilidad.cuentaEnEstadisticas);
    const alumnos = usuariosReales.filter((usuario) => getUserRole(usuario) === 'student').length;
    const profesores = usuariosReales.filter((usuario) => getUserRole(usuario) === 'teacher').length;
    const masters = usuariosReales.filter((usuario) => getUserRole(usuario) === 'master').length;
    const activos = usuariosReales.filter((usuario) => normalizeStatus(usuario.estatus) === 'activo').length;
    const accesosIncompletos = usuariosReales.filter(
      (usuario) => usuario.authAccessStatus !== 'complete'
    ).length;

    return {
      total: usuariosReales.length,
      alumnos,
      profesores,
      masters,
      activos,
      accesosIncompletos,
    };
  }, [usuarios, visibilidad.cuentaEnEstadisticas]);

  const hasActiveFilters = Boolean(search || statusFilter || roleFilter || profesorAsignadoFilter);

  const customButtons = useMemo(() => [
    {
      label: 'Material para imprimir',
      onClick: () => navigate('/dashboard/master/agregar-material-didactico'),
      variant: 'outline-light',
    },
    {
      label: 'Lecturas adicionales',
      onClick: () => navigate('/dashboard/master/agregar-lecturas-adicionales'),
      variant: 'outline-light',
    },
    {
      label: 'Material teórico',
      onClick: () => navigate('/dashboard/master/agregar-material-teorico'),
      variant: 'outline-light',
    },
  ], [navigate]);

  const renderRoleIcon = useCallback((role) => {
    if (role === 'master') return <FaUserShield />;
    if (role === 'teacher') return <FaChalkboardTeacher />;
    return <FaUserGraduate />;
  }, []);

  const renderUsuario = useCallback((usuario) => {
    const role = getUserRole(usuario);
    const nombre = getUserName(usuario);
    const statusKey = normalizeStatus(usuario.estatus);
    const teacherSwitchId = `teacher-switch-${usuario.id}`;

    return (
      <article className="master-user-card" key={usuario.id}>
        <div className="master-user-card__identity">
          <div className={`master-user-card__avatar master-user-card__avatar--${role}`}>
            {getInitials(nombre)}
          </div>

          <div className="master-user-card__details">
            <h3>{nombre}</h3>
            <div className="master-user-card__email">
              <FaEnvelope />
              <span>{usuario.email || 'Sin correo registrado'}</span>
            </div>
            <div className="master-user-card__secondary">
              {usuario.matricula || usuario.username || usuario.id}
            </div>
          </div>
        </div>

        <div className="master-user-card__badges">
          <span className={`role-badge role-badge--${role}`}>
            {renderRoleIcon(role)}
            {getRoleLabel(role)}
          </span>
          <span className={`status-badge status-badge--${statusKey}`}>
            {getStatusLabel(usuario.estatus)}
          </span>
          <span className={`access-badge access-badge--${usuario.authAccessStatus || 'missing-auth'}`}>
            {getAuthAccessLabel(usuario.authAccessStatus)}
          </span>
        </div>

        <div className="master-user-card__teacher">
          <span>Profesor asignado</span>
          <strong>{usuario.asignTeacher || 'No asignado'}</strong>
        </div>

        <div className="master-user-card__switch">
          <Form.Check
            type="switch"
            id={teacherSwitchId}
            label="Profesor"
            checked={Boolean(usuario.teacher)}
            disabled={updatingTeacherId === usuario.id}
            onChange={(event) => handleCheckChanges(event, usuario)}
          />
        </div>

        <div className="master-user-card__actions">
          <BotonVisibilidadAlumno alumno={usuario} visibilidad={visibilidad} />
          <Button
            variant="outline-primary"
            onClick={() => handleOpenEditModal(usuario)}
            className="btn-transition"
          >
            <FaUserEdit /> Editar
          </Button>
        </div>
      </article>
    );
  }, [handleCheckChanges, handleOpenEditModal, renderRoleIcon, updatingTeacherId, visibilidad]);

  if (loading && !usuarios.length) {
    return (
      <div className="masterview-page">
        <NavbarGlobal
          title="Listado general de usuarios"
          showBackButton={true}
          customButtons={customButtons}
        />
        <div className="masterview-state">
          <Spinner animation="border" variant="primary" />
          <p>Cargando usuarios...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="masterview-page">
        <NavbarGlobal
          title="Listado general de usuarios"
          showBackButton={true}
          customButtons={customButtons}
        />
        <div className="masterview-state masterview-state--error">
          <FaUsers />
          <h2>No se pudo cargar el listado</h2>
          <p>{error}</p>
          <Button variant="primary" onClick={loadUsers}>Reintentar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="masterview-page">
      <NavbarGlobal
        title="Listado general de usuarios"
        showBackButton={true}
        customButtons={customButtons}
      />

      <AddUserModal show={showAddModal} handleClose={handleCloseAddModal} />
      <EditUserModal show={showEditModal} handleClose={handleCloseEditModal} user={selectedUser} />

      <Container className="masterview-page__container">
        <Breadcrumb className="masterview-page__breadcrumb">
          <Breadcrumb.Item href="/dashboard">Inicio</Breadcrumb.Item>
          <Breadcrumb.Item active>Usuarios</Breadcrumb.Item>
        </Breadcrumb>

        <section className="master-hero glass">
          <div className="master-hero__copy">
            <span className="master-hero__eyebrow">Panel master</span>
            <h1>Listado general de usuarios</h1>
            <p>Administra alumnos, profesores, estatus y asignaciones desde una vista preparada para escritorio y móvil.</p>
            {resumenUsuarios.accesosIncompletos > 0 && (
              <p className="master-hero__access-warning" role="alert">
                {resumenUsuarios.accesosIncompletos} usuario(s) requieren completar su acceso.
              </p>
            )}
            <div className="d-flex flex-wrap gap-2">
              <Button variant="primary" onClick={handleOpenAddModal} className="master-hero__action btn-transition">
                <FaUserPlus /> Agregar usuario
              </Button>
              <Button
                variant="outline-primary"
                onClick={() => navigate('/dashboard/master/pendientes-ejercicios-practicos')}
                className="master-hero__action btn-transition"
              >
                Revisar pendientes SSO
              </Button>
            </div>
          </div>

          <div className="master-hero__stats" aria-label="Resumen de usuarios">
            <div className="master-stat master-stat--total">
              <span>{resumenUsuarios.total}</span>
              <small>Total</small>
            </div>
            <div className="master-stat">
              <span>{resumenUsuarios.alumnos}</span>
              <small>Alumnos</small>
            </div>
            <div className="master-stat">
              <span>{resumenUsuarios.profesores}</span>
              <small>Profesores</small>
            </div>
            <div className="master-stat">
              <span>{resumenUsuarios.masters}</span>
              <small>Master</small>
            </div>
            <div className="master-stat">
              <span>{resumenUsuarios.activos}</span>
              <small>Activos</small>
            </div>
          </div>
        </section>

        <FiltroAlumnosOcultos visibilidad={visibilidad} cantidad={cantidadOcultos} />
        <section className="master-toolbar glass" aria-label="Filtros del listado general">
          <Form.Group className="filter-field filter-field--search" controlId="masterSearchInput">
            <Form.Label>Buscar usuario</Form.Label>
            <InputGroup>
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Nombre, correo, matrícula o profesor"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                autoComplete="off"
              />
              {search && (
                <Button
                  variant="outline-primary"
                  onClick={() => setSearch('')}
                  aria-label="Limpiar búsqueda"
                >
                  <FaTimes />
                </Button>
              )}
            </InputGroup>
          </Form.Group>

          <Form.Group className="filter-field" controlId="estatusFilter">
            <Form.Label>Estatus</Form.Label>
            <Form.Select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              {STATUS_OPTIONS.map((option) => (
                <option key={option.value || 'todos'} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="filter-field" controlId="roleFilter">
            <Form.Label>Rol</Form.Label>
            <Form.Select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value || 'todos'} value={option.value}>{option.label}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="filter-field" controlId="profesorAsignadoFilter">
            <Form.Label>Profesor asignado</Form.Label>
            <Form.Select value={profesorAsignadoFilter} onChange={(event) => setProfesorAsignadoFilter(event.target.value)}>
              <option value="">Todos</option>
              {profesoresUnicos.map((profesor) => (
                <option key={profesor} value={profesor}>{profesor}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <div className="filter-actions">
            <Button
              variant="outline-primary"
              onClick={clearFilters}
              disabled={!hasActiveFilters}
              className="btn-transition"
            >
              <FaTimes /> Limpiar
            </Button>
          </div>
        </section>

        <section className="master-results">
          <div className="master-results__summary">
            <div>
              <span>{filteredUsuarios.length}</span> de {usuariosVisibles.length} usuarios en esta vista
            </div>
            <small>{hasActiveFilters ? 'Filtros aplicados' : 'Sin filtros activos'}</small>
          </div>

          {filteredUsuarios.length === 0 ? (
            <div className="masterview-state glass">
              <FaSearch />
              <h2>Sin resultados</h2>
              <p>No se encontraron usuarios con los filtros actuales.</p>
              <Button variant="primary" onClick={clearFilters}>Limpiar filtros</Button>
            </div>
          ) : (
            <div className="master-users-list glass">
              <div className="master-users-list__header" aria-hidden="true">
                <span>Usuario</span>
                <span>Rol y estatus</span>
                <span>Profesor asignado</span>
                <span>Flag profesor</span>
                <span>Acciones</span>
              </div>

              <div className="master-users-list__body">
                {filteredUsuarios.map(renderUsuario)}
              </div>
            </div>
          )}
        </section>

        <div className="master-quick-actions" aria-label="Accesos rápidos de contenido">
          <Button variant="outline-primary" onClick={() => navigate('/dashboard/master/pendientes-ejercicios-practicos')}>
            <FaUserShield /> Pendientes SSO
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/dashboard/master/agregar-material-didactico')}>
            <FaPrint /> Material para imprimir
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/dashboard/master/agregar-lecturas-adicionales')}>
            <FaBookOpen /> Lecturas adicionales
          </Button>
          <Button variant="outline-primary" onClick={() => navigate('/dashboard/master/agregar-material-teorico')}>
            <FaBookOpen /> Material teórico
          </Button>
        </div>
      </Container>
    </div>
  );
}

export { MasterView };
